import React, { useRef, useState } from 'react';

type InfoTooltipProps = {
  text: string;
  label?: string;
};

const BUBBLE_MAX_WIDTH = 260;
const VIEWPORT_MARGIN = 8;

// Positioned with `position: fixed` and coordinates measured from the icon's
// real screen position on hover/focus, rather than `position: absolute`
// relative to the nearest positioned ancestor. This page has scrollable
// containers (e.g. the results table wraps horizontally), and an absolutely
// positioned bubble gets silently clipped by any ancestor with
// `overflow-x: auto` (it forces `overflow-y` to `auto` too, per the CSS
// spec, even with no visible scrollbar). `position: fixed` escapes that
// entirely since it is only clipped by the viewport.
//
// The bubble's left edge is clamped to stay within the viewport (icons near
// a screen edge would otherwise center a 260px bubble partly off-screen).
// The arrow is repositioned via a CSS variable to keep pointing at the icon
// even when the bubble itself has been shifted to stay on-screen.
export const InfoTooltip: React.FC<InfoTooltipProps> = ({ text, label = 'More info' }) => {
  const iconRef = useRef<HTMLSpanElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number; arrowLeft: number } | null>(null);

  const updatePosition = () => {
    const rect = iconRef.current?.getBoundingClientRect();
    if (!rect) return;

    const centerX = rect.left + rect.width / 2;
    const idealLeft = centerX - BUBBLE_MAX_WIDTH / 2;
    const maxLeft = window.innerWidth - BUBBLE_MAX_WIDTH - VIEWPORT_MARGIN;
    const left = Math.max(VIEWPORT_MARGIN, Math.min(idealLeft, maxLeft));
    const arrowLeft = Math.max(16, Math.min(centerX - left, BUBBLE_MAX_WIDTH - 16));

    setCoords({ top: rect.top, left, arrowLeft });
  };

  return (
    <span
      className="kg-tooltip"
      tabIndex={0}
      onMouseEnter={updatePosition}
      onFocus={updatePosition}
      onMouseLeave={() => setCoords(null)}
      onBlur={() => setCoords(null)}
    >
      <span ref={iconRef} className="kg-tooltip-icon" aria-label={label}>i</span>
      {coords && (
        <span
          className="kg-tooltip-bubble"
          role="tooltip"
          style={{
            top: coords.top,
            left: coords.left,
            ['--kg-tooltip-arrow-left' as string]: `${coords.arrowLeft}px`,
          }}
        >
          {text}
        </span>
      )}
    </span>
  );
};
